// 세금계산서 / 현금영수증 (CompanyInvoiceController)
//
// 실제 발급은 국세청 인증 연동사를 거친다. 화면에서는 연동사를 드러내지 않는다.

export type InvoiceType = "TAX_INVOICE" | "CASH_RECEIPT";
export type InvoiceStatus = "PENDING" | "SUCCESS" | "FAIL" | "CANCELED";
export type CashReceiptUsage = "INCOME_DEDUCTION" | "EXPENSE_PROOF";
export type NtsSendState = "BEFORE_SEND" | "SENDING" | "SENT" | "SEND_FAILED" | "CANCELED";

/** 구매자(증빙 받는 사람) 정보. 쇼핑몰·MES 가 같은 모양으로 보낸다 */
export interface TaxDocumentBuyer {
  invoiceType: InvoiceType;
  cashReceiptUsage?: CashReceiptUsage | null;
  identityNum?: string | null;
  buyerBusinessNumber?: string | null;
  buyerBusinessName?: string | null;
  buyerCeoName?: string | null;
  buyerAddress?: string | null;
  buyerBizType?: string | null;
  buyerBizClass?: string | null;
  buyerContactName?: string | null;
  buyerEmail?: string | null;
  buyerMobile?: string | null;
}

export interface InvoiceItem extends TaxDocumentBuyer {
  id: number;
  orderId: number;
  orderNumber: string | null;
  orderName: string;
  orderTotalAmount: number;
  orderCreatedAt: string | null;
  orderStatus: string;
  paymentMethod: "TOSS" | "BANK_TRANSFER" | null;
  purchaserName: string | null;
  status: InvoiceStatus;
  issuedAt: string | null;
  memo: string | null;
  createdAt: string | null;
  totalAmount: number;
  vatAmount: number;
  mgtKey: string | null;
  ntsState: NtsSendState | null;
  approvalNo: string | null;
  approvalAt: string | null;
  canceledAt: string | null;
  errorMessage: string | null;
  issuedViaIntegration: boolean;
  orderPaid: boolean;
  /** 처리 대기(DRAFT/FAILED) 환불 조정 건 수 */
  pendingAdjustmentCount: number;
  /** 발급 완료(SUCCESS) 증빙의 주문이 취소 상태가 됨. 무통장입금은 취소가 환불인지 알 수 없어 자동 처리하지 않는다 */
  orderCanceledAfterIssue: boolean;
}

export interface InvoiceIssueResult {
  id: number;
  success: boolean;
  message: string | null;
}

export interface TaxDocumentConnection {
  connected: boolean;
  needsReconnect: boolean;
  autoIssue: boolean;
  certificateRegistered: boolean | null;
  certificateExpireDate: string | null;
  certificateMessage: string | null;
}

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  TAX_INVOICE: "세금계산서",
  CASH_RECEIPT: "현금영수증",
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  PENDING: "미발행",
  SUCCESS: "발행완료",
  FAIL: "발행실패",
  CANCELED: "취소",
};

export const CASH_RECEIPT_USAGE_LABELS: Record<CashReceiptUsage, string> = {
  INCOME_DEDUCTION: "소득공제용",
  EXPENSE_PROOF: "지출증빙용",
};

export const NTS_STATE_LABELS: Record<NtsSendState, string> = {
  BEFORE_SEND: "전송 전",
  SENDING: "전송 중",
  SENT: "전송 완료",
  SEND_FAILED: "전송 실패",
  CANCELED: "취소",
};

// 환불 조정 (원본 증빙에 딸린 취소/부분취소/수정세금계산서 건)

export type InvoiceAdjustmentKind = "CASH_CANCEL_FULL" | "CASH_CANCEL_PARTIAL" | "TAX_ISSUE_CANCEL" | "TAX_MODIFY";
export type InvoiceAdjustmentStatus = "DRAFT" | "DONE" | "FAILED" | "DISCARDED";
/** 수정세금계산서 사유. 2 공급가액 변동 / 3 환입 / 4 계약의 해제 */
export type InvoiceModifyCode = "2" | "3" | "4";

export interface InvoiceAdjustment {
  id: number;
  invoiceId: number;
  orderRefundId: number | null;
  kind: InvoiceAdjustmentKind;
  status: InvoiceAdjustmentStatus;
  /** TAX_MODIFY 는 음수로 내려온다 */
  supplyAmount: number;
  vatAmount: number;
  totalAmount: number;
  modifyCode: InvoiceModifyCode | null;
  writeDate: string | null;
  dueDate: string | null;
  mgtKey: string | null;
  ntsState: NtsSendState | null;
  approvalNo: string | null;
  errorMessage: string | null;
  processedAt: string | null;
  createdAt: string;
  executable: boolean;
  notExecutableReason: string | null;
}

export interface InvoiceAdjustmentSummary {
  pendingCount: number;
  dueSoonCount: number;
  nearestDueDate: string | null;
}

export const INVOICE_ADJUSTMENT_KIND_LABELS: Record<InvoiceAdjustmentKind, string> = {
  CASH_CANCEL_FULL: "현금영수증 전액 취소",
  CASH_CANCEL_PARTIAL: "현금영수증 부분 취소",
  TAX_ISSUE_CANCEL: "세금계산서 발행 취소",
  TAX_MODIFY: "수정세금계산서",
};

export const INVOICE_ADJUSTMENT_STATUS_LABELS: Record<InvoiceAdjustmentStatus, string> = {
  DRAFT: "처리 필요",
  DONE: "완료",
  FAILED: "실패",
  DISCARDED: "폐기",
};

export const INVOICE_MODIFY_CODE_LABELS: Record<InvoiceModifyCode, string> = {
  "2": "공급가액 변동",
  "3": "환입(반품)",
  "4": "계약의 해제",
};

// 주문 부분 환불 기록 (CompanyOrderRefundController)

export interface OrderRefund {
  id: number;
  amount: number;
  reason: string;
  /** YYYY-MM-DD */
  refundedAt: string;
  createdAt: string;
}

export interface OrderRefundSummary {
  orderTotalAmount: number;
  refundedTotal: number;
  refundableAmount: number;
  partialRefundAllowed: boolean;
  notAllowedReason: string | null;
  refunds: OrderRefund[];
}
